import { useCallback, useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

interface DesktopDrawerProps {
  open: boolean;
  onRequestClose: () => void;
  children: ReactNode;
  ariaLabel?: string;
  aiIdPrefix?: string;
}

export function DesktopDrawer({
  open,
  onRequestClose,
  children,
  ariaLabel = 'drawer',
  aiIdPrefix = 'desktop-drawer',
}: DesktopDrawerProps) {
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      onRequestClose();
    }
  }, [onRequestClose]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = originalOverflow;
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, open]);

  if (!open) {
    return null;
  }

  return createPortal(
    <div
      className="fixed inset-0 z-40 hidden md:block"
      role="dialog"
      aria-modal="true"
      aria-label={ariaLabel}
      data-ai-id={aiIdPrefix}
    >
      <button
        type="button"
        aria-label="Close dialog"
        className="absolute inset-0 cursor-pointer bg-black/30 focus:outline-none"
        onClick={onRequestClose}
        data-ai-id={`${aiIdPrefix}-backdrop`}
      />

      <aside
        className="absolute inset-y-0 right-0 h-full w-[90vw] border-l border-slate-300 bg-slate-50"
        data-ai-id={`${aiIdPrefix}-panel`}
      >
        <div className="h-full overflow-y-auto p-4 lg:p-6" data-ai-id={`${aiIdPrefix}-content`}>
          {children}
        </div>
      </aside>
    </div>,
    document.body,
  );
}
